import { tempProcessVideoSrc } from '@/components/DocumentPreview/utils';

export interface BrightcovePlayerIds {
  accountId: string;
  playerId: string;
  embed: string;
  videoId: string;
}

const PLAYER_PATH =
  /^https?:\/\/players\.brightcove\.net\/([^/]+)\/([^/]+)_([^/]+)\/index\.html/i;

/** True when `url` is a Brightcove iframe / player page URL. */
export const isBrightcovePlayerUrl = (
  url: string | undefined | null,
): url is string =>
  !!url && url.toLowerCase().includes('players.brightcove.net');

/**
 * Parses a Brightcove player page URL into in-page embed ids.
 * Applies {@link tempProcessVideoSrc} first so player id matches current embeds.
 */
export const parseBrightcovePlayerUrl = (
  src: string | undefined | null,
): BrightcovePlayerIds | undefined => {
  if (!src) {
    return undefined;
  }

  const processed = tempProcessVideoSrc(src) ?? src;

  try {
    const url = new URL(processed);
    if (!url.hostname.toLowerCase().includes('players.brightcove.net')) {
      return undefined;
    }

    const pathMatch = PLAYER_PATH.exec(url.href);
    if (!pathMatch) {
      return undefined;
    }

    const [, accountId, playerId, embed] = pathMatch;
    const videoId = url.searchParams.get('videoId');
    if (!accountId || !playerId || !embed || !videoId) {
      return undefined;
    }

    return { accountId, playerId, embed, videoId };
  } catch {
    return undefined;
  }
};

export const brightcovePlayerScriptUrl = ({
  accountId,
  playerId,
  embed,
}: Pick<BrightcovePlayerIds, 'accountId' | 'playerId' | 'embed'>): string =>
  `https://players.brightcove.net/${accountId}/${playerId}_${embed}/index.min.js`;

const IFRAME_SRC = /<iframe\b[^>]*\bsrc=["']([^"']+)["'][^>]*>/gi;

/**
 * True when HTML embeds Brightcove players from more than one account.
 * Mixing accounts with the same player id (`default`) is unsupported in-page —
 * Brightcove's global `bc` registry keys on `playerId_embed`, not account, so
 * the last-loaded script owns the registry and wrong-account Playback calls
 * follow. Callers should keep original iframes for mixed-account documents.
 */
export const htmlHasMultipleBrightcoveAccounts = (html: string): boolean => {
  const accounts = new Set<string>();
  for (const match of html.matchAll(IFRAME_SRC)) {
    const src = match[1];
    if (!src) {
      continue;
    }
    const ids = parseBrightcovePlayerUrl(src);
    if (!ids) {
      continue;
    }
    accounts.add(ids.accountId);
    if (accounts.size > 1) {
      return true;
    }
  }
  return false;
};
